import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";

// Tailwind tone -> badge classes (kept explicit so Tailwind keeps the classes).
export const PRIORITY_TONE = {
  rose: "bg-rose-50 text-rose-700 border-rose-200",
  red: "bg-red-50 text-red-700 border-red-200",
  orange: "bg-orange-50 text-orange-700 border-orange-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  yellow: "bg-yellow-50 text-yellow-700 border-yellow-200",
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  sky: "bg-sky-50 text-sky-700 border-sky-200",
  indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
  violet: "bg-violet-50 text-violet-700 border-violet-200",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  slate: "bg-slate-100 text-slate-700 border-slate-200",
};

const FALLBACK = {
  critical: { name: "Critical", color: "rose" },
  high: { name: "High", color: "orange" },
  medium: { name: "Medium", color: "blue" },
  low: { name: "Low", color: "slate" },
};

const PrioritiesContext = createContext({
  priorities: [],
  priorityMap: FALLBACK,
  priorityOptions: Object.entries(FALLBACK).map(([value, m]) => ({ value, label: m.name })),
  toneClass: (c) => PRIORITY_TONE[c] || PRIORITY_TONE.slate,
  refresh: () => {},
});

export function PrioritiesProvider({ children }) {
  const [priorities, setPriorities] = useState([]);

  const refresh = useCallback(() => {
    api.get("/sla-priorities").then((r) => setPriorities(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const value = useMemo(() => {
    const active = priorities.filter((p) => p.active !== false);
    const map = {};
    priorities.forEach((p) => { map[p.key] = { name: p.name, color: p.color }; });
    const merged = { ...FALLBACK, ...map };
    return {
      priorities,
      activePriorities: active,
      priorityMap: merged,
      priorityOptions: (active.length ? active : Object.entries(FALLBACK).map(([key, m]) => ({ key, name: m.name })))
        .map((p) => ({ value: p.key, label: p.name })),
      toneClass: (c) => PRIORITY_TONE[c] || PRIORITY_TONE.slate,
      refresh,
    };
  }, [priorities, refresh]);

  return <PrioritiesContext.Provider value={value}>{children}</PrioritiesContext.Provider>;
}

export const usePriorities = () => useContext(PrioritiesContext);
